import { action, observable, reaction, runInAction } from "mobx";

import { getCurrentCulture, isGamesTheme } from "@utils";
import { LazyImportWithLoadFailHandle } from "@lib/lazy-import-with-guard";
import type { RootStore } from "@state/stores/types";

type SignalRModule = typeof import("@microsoft/signalr");
type HubConnection = import("@microsoft/signalr").HubConnection;

export type SignalRCoreState =
	| "Disconnected"
	| "Connecting"
	| "Connected"
	| "Disconnecting"
	| "Reconnecting";

let signalrPromise: Promise<SignalRModule> | null = null;
const loadSignalR = (): Promise<SignalRModule> => {
	if (!signalrPromise) {
		// Wrap as { default: ... } so the existing lazy-import helper (built for
		// default-export modules) can still redirect on chunk-load failure.
		const p = LazyImportWithLoadFailHandle<SignalRModule>(
			`/${getCurrentCulture()}/app-update`,
			() =>
				import(
					/* webpackChunkName: "signalr-core" */ "@microsoft/signalr"
				).then((m) => ({ default: m }))
		).then((wrapped) => wrapped.default);
		// Reset the singleton on rejection so a transient first-call failure
		// doesn't permanently disable the hub for the session.
		p.catch(() => {
			if (signalrPromise === p) {
				signalrPromise = null;
			}
		});
		signalrPromise = p;
	}
	return signalrPromise;
};

export abstract class BaseSignalRCoreHubStore {
	@observable public state: SignalRCoreState = "Disconnected";

	protected rootStore: RootStore;
	protected connection: HubConnection | null = null;

	private loginReactionDisposer: (() => void) | null = null;
	private startInFlight: Promise<void> | null = null;
	// Bumped on every stop(); an in-flight start() compares its captured epoch
	// against the live value and abandons (stopping the orphan it built) if they
	// differ, preventing zombie connections and ensuring restart() truly restarts.
	private startEpoch = 0;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
	}

	protected abstract hubUrl(): string;
	protected abstract registerHandlers(connection: HubConnection): void;
	protected onStarted(_connection: HubConnection): void | Promise<void> {
		// Subclasses override to send the initial per-connection state
		// (subscribe, fetch baseline). Runs once per fresh connection inside
		// the deduped start() promise, so concurrent start() callers share
		// a single execution.
		// Default: no-op.
	}
	protected onReconnected(_connection: HubConnection): void | Promise<void> {
		// Subclasses override to resend per-connection state on automatic reconnect.
		// Default: no-op.
	}
	protected onStopped(): void {
		// Subclasses override to cancel timers / drop per-connection caches when
		// the hub stops or is destroyed. Default: no-op.
	}

	@action.bound
	public async initialize(): Promise<void> {
		if (isGamesTheme()) {
			return;
		}
		if (!this.hubUrl()) {
			return;
		}

		this.loginReactionDisposer?.();
		// Token changes (login/logout/refresh) need a fresh handshake so the
		// new token reaches accessTokenFactory. restart() can reject if the
		// new connection fails; swallow so the reaction doesn't die — `state`
		// carries the failure for any caller that cares.
		this.loginReactionDisposer = reaction(
			() => this.rootStore.userAuthStore.token?.token ?? "",
			() => {
				this.restart().catch(() => {
					/* state already set in start() */
				});
			}
		);

		// Don't let a failed handshake block app boot — observers can read
		// `state === "Disconnected"`.
		try {
			await this.start();
		} catch {
			/* logged in start() */
		}
	}

	@action.bound
	public async start(): Promise<void> {
		if (this.startInFlight) {
			return this.startInFlight;
		}

		const epoch = ++this.startEpoch;

		this.startInFlight = (async () => {
			let builtConnection: HubConnection | undefined;
			try {
				const signalr = await loadSignalR();
				if (epoch !== this.startEpoch) return;

				const connection = new signalr.HubConnectionBuilder()
					.withUrl(this.hubUrl(), {
						accessTokenFactory: () => this.getAccessToken(),
						// Auth is bearer-only (token via accessTokenFactory); the hub
						// reads no cookies, so opt out of credentialed requests — keeps
						// the server CORS policy free of AllowCredentials.
						withCredentials: false,
					})
					.withAutomaticReconnect()
					.configureLogging(signalr.LogLevel.Warning)
					.build();
				builtConnection = connection;

				connection.onreconnecting(() => {
					if (connection !== this.connection) return;
					runInAction(() => {
						this.state = "Reconnecting";
					});
				});
				connection.onreconnected(() => {
					if (connection !== this.connection) return;
					runInAction(() => {
						this.state = "Connected";
					});
					Promise.resolve(this.onReconnected(connection)).catch(
						(err) => {
							console.error(
								"SignalR Core onReconnected handler failed:",
								err
							);
						}
					);
				});
				connection.onclose((err) => {
					if (connection !== this.connection) return;
					if (err) {
						console.error(
							"SignalR Core hub connection closed with error:",
							err
						);
					}
					runInAction(() => {
						this.state = "Disconnected";
					});
				});

				this.registerHandlers(connection);

				runInAction(() => {
					this.state = "Connecting";
				});
				if (epoch !== this.startEpoch) return;
				await connection.start();

				if (epoch !== this.startEpoch) {
					// stop() ran while we were handshaking — abandon the orphan.
					try {
						await connection.stop();
					} catch {
						/* nothing to recover */
					}
					return;
				}

				runInAction(() => {
					this.connection = connection;
					this.state = "Connected";
				});

				// Run subscriber-side init inside the deduped start promise
				// so concurrent start() callers share a single execution.
				try {
					await this.onStarted(connection);
				} catch (err) {
					console.error(
						"SignalR Core onStarted handler failed:",
						err
					);
				}
			} catch (err) {
				if (epoch !== this.startEpoch) {
					// Abandoned attempt; suppress its error and clean up.
					if (builtConnection) {
						try {
							await builtConnection.stop();
						} catch {
							/* ignored */
						}
					}
					return;
				}
				runInAction(() => {
					this.state = "Disconnected";
				});
				console.error("SignalR Core hub failed to start:", err);
				throw err;
			} finally {
				this.startInFlight = null;
			}
		})();

		return this.startInFlight;
	}

	@action.bound
	public async stop(): Promise<void> {
		// Invalidate any in-flight start before it can assign this.connection.
		this.startEpoch++;
		const stopEpoch = this.startEpoch;
		if (this.startInFlight) {
			try {
				await this.startInFlight;
			} catch {
				/* the abandoned start swallows its own error */
			}
		}

		const current = this.connection;
		this.connection = null;
		try {
			this.onStopped();
		} catch (err) {
			// Don't let a misbehaving subclass hook leak the connection
			// (current.stop() still needs to run below).
			console.error("SignalR Core onStopped handler failed:", err);
		}
		if (!current) {
			if (stopEpoch === this.startEpoch) {
				runInAction(() => {
					this.state = "Disconnected";
				});
			}
			return;
		}
		runInAction(() => {
			this.state = "Disconnecting";
		});
		try {
			await current.stop();
		} catch (err) {
			console.error("SignalR Core hub failed to stop cleanly:", err);
		}
		// Only finalize state if no fresh start() has bumped the epoch during
		// our await — otherwise we'd clobber the new connection's "Connected".
		if (stopEpoch === this.startEpoch) {
			runInAction(() => {
				this.state = "Disconnected";
			});
		}
	}

	@action.bound
	public async restart(): Promise<void> {
		await this.stop();
		await this.start();
	}

	@action.bound
	public async deactivate(): Promise<void> {
		// Off-route teardown: unlike stop(), also drop the login reaction —
		// otherwise a token change would silently reconnect a hub no screen is
		// watching. A later initialize() reinstalls the reaction and reconnects.
		this.loginReactionDisposer?.();
		this.loginReactionDisposer = null;
		await this.stop();
	}

	// Intentionally NOT @action.bound: MobX 4's bound-action decorator installs
	// an own-property on `this` the first time super.method() is invoked from a
	// subclass override, which then shadows the subclass override permanently.
	// The body has no direct observable writes — stop() handles its own action
	// wrapping — so a plain method here keeps subclass overrides addressable
	// across repeated calls.
	public async destroyConnectionOnUserLogout(): Promise<void> {
		// NOTE: do NOT dispose loginReactionDisposer here — the token reaction must
		// survive a logout so the hub can transition back to an anonymous
		// connection (and resume per-user data flow if the user logs in again).
		await this.stop();
	}

	private getAccessToken(): string {
		// Same source as the REST layer (getDefaultRequestContext): the active
		// login token, not the role-derived online/shop variant. Score-predictor
		// segments by user, not by selected wallet.
		return this.rootStore.userAuthStore.token?.token ?? "";
	}
}
